/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * ClientAdapter.tsx
 */

import ListAdapter, {AdapterListener} from "../../tools/list/ListAdapter";
import React from "react";
import {Column} from "../../tools/list/Column"
import Translation from "../../services/translation/Translation";
import moment from "moment";
import {AccessTokenWrapper} from "../../services/AccessTokenService";
import Button from "../../tools/view/element/button/Button";
import Colors from "../../tools/model/enumerations/Colors";
import UIToast from "../../tools/view/toast/UIToast";
import AccessTokenDataProvider from "../../provider/AccessTokenDataProvider";
import {AccessTokenAPIPagination} from "../../pagination/AccessTokenAPIPagination";

export interface AccessTokenAdapterListener extends AdapterListener<AccessTokenWrapper> {
    revokeToken: (token: AccessTokenWrapper) => void;
}

export default class AccessTokenAdapter extends ListAdapter<AccessTokenWrapper, AccessTokenAdapterListener, AccessTokenAPIPagination> {
    private _t: Translation;

    /**
     * constructor
     * @param tokensProvider
     * @param listener
     * @param t
     */
    constructor(t: Translation, tokensProvider: AccessTokenDataProvider, listener?: AccessTokenAdapterListener) {
        super();

        this._t = t;
        this.addColumn(new Column(t.tokenListAccessToken, "accessToken", this._accessToken));
        this.addColumn(new Column(t.tokenListRefreshToken, "refreshToken", this._refreshToken));
        this.addColumn(new Column(t.tokenListCreatedAt, "createdAt", this._createdAt));
        this.addColumn(new Column(t.tokenListExpiresIn, "expiresIn", this._expiresIn));
        this.addColumn(new Column(t.status, "revoked", this._revoke));

        this.init(t.tokenListEmpty,
            tokensProvider,
            [
                {relatedFilterKey: "revoked", translationKey: "all", value: "all"},
                {relatedFilterKey: "revoked", translationKey: "valid", value: "false"},
                {relatedFilterKey: "revoked", translationKey: "revoked", value: "true"}
            ],
            listener);
    }

    /**
     * _accessToken
     * @returns {any}
     * @private
     * @param token
     */
    private _accessToken = (token: AccessTokenWrapper): JSX.Element => {
        return <p className={"blue-hover"}
                  onClick={() => this._copyAccessTokenToClipboard(token.access_token)}>
            {token.access_token.substr(0, 15)}...
        </p>
    };

    /**
     * _copyAccessTokenToClipboard
     * @param s
     * @private
     */
    private _copyAccessTokenToClipboard = (s: string) => {
        navigator.clipboard.writeText(s);
        UIToast.default.show(this._t.accessTokenCopied)
    };

    /**
     * _copyRefreshTokenToClipboard
     * @param s
     * @private
     */
    private _copyRefreshTokenToClipboard = (s: string) => {
        navigator.clipboard.writeText(s);
        UIToast.default.show(this._t.refreshTokenCopied)
    };

    /**
     * _refreshToken
     * @returns {any}
     * @private
     * @param token
     */
    private _refreshToken = (token: AccessTokenWrapper): JSX.Element => {
        return <p className={"blue-hover"}
                  onClick={() => this._copyRefreshTokenToClipboard(token.access_token)}>
            {token.refresh_token.substr(0, 15)}...
        </p>
    };

    /**
     * _createdAt
     * @returns {any}
     * @private
     * @param token
     */
    private _createdAt = (token: AccessTokenWrapper): JSX.Element => {
        return <p className={"blue-hover"}>{moment(token.created_at).format("MM/DD/YYYY hh:mm")}</p>
    };

    /**
     * _expiresIn
     * @returns {any}
     * @private
     * @param token
     */
    private _expiresIn = (token: AccessTokenWrapper): JSX.Element => {
        return <p className={"blue-hover"}>{token.expires_in / 3600}h</p>
    };

    /**
     * _revoke
     * @returns {any}
     * @private
     * @param token
     */
    private _revoke = (token: AccessTokenWrapper): JSX.Element => {
        if (token.revoked) {
            return <Button color={Colors.light_grey} disabled={true} labelAccessor={t => t.revoked}/>
        }

        return <Button color={Colors.green} labelAccessor={t => t.valid}/>
    };
}